import { supabase } from '../lib/supabase';
import type { IcpMasterData, IcpConfig } from '../types/icp';

let cachedMasterData: IcpMasterData | null = null;

export async function getIcpMasterData(): Promise<IcpMasterData> {
  if (cachedMasterData) return cachedMasterData;

  const { data, error } = await supabase.rpc('get_icp_master_data');

  if (error) throw new Error(`Failed to load ICP master data: ${error.message}`);

  cachedMasterData = data as IcpMasterData;
  return cachedMasterData;
}

export function invalidateIcpCache() {
  cachedMasterData = null;
}

export async function getIcpConfig(clientId: string): Promise<IcpConfig | null> {
  const { data, error } = await supabase
    .from('user_settings')
    .select('icp_config')
    .eq('client_id', clientId)
    .not('icp_config', 'is', null)
    .limit(1);

  if (error) throw new Error(`Failed to load ICP config: ${error.message}`);
  return data?.[0]?.icp_config || null;
}

export async function saveIcpConfig(clientId: string, config: IcpConfig): Promise<void> {
  const { error } = await supabase
    .from('user_settings')
    .update({ icp_config: config })
    .eq('client_id', clientId);

  if (error) throw new Error(`Failed to save ICP config: ${error.message}`);
}

export function resolveIcpNames(
  config: IcpConfig,
  masterData: IcpMasterData
): {
  countries: string[];
  industries: string[];
  titles: string[];
  company_sizes: string[];
} {
  const countryMap = new Map(masterData.countries.map(c => [c.id, c]));
  const industryMap = new Map(masterData.industries.map(i => [i.id, i]));
  const titleMap = new Map(masterData.titles.map(t => [t.id, t]));

  const countries: string[] = [];
  for (const sel of config.countries) {
    const country = countryMap.get(sel.country_id);
    if (!country) continue;

    if (sel.region_ids.length === 0) {
      countries.push(country.country_name);
    } else {
      const regionMap = new Map(country.regions.map(r => [r.id, r]));
      for (const rid of sel.region_ids) {
        const region = regionMap.get(rid);
        if (region) {
          countries.push(`${country.country_name} - ${region.region_name}`);
        }
      }
    }
  }

  return {
    countries,
    industries: config.industry_ids
      .map(id => industryMap.get(id)?.industry_name)
      .filter((n): n is string => !!n),
    titles: config.title_ids
      .map(id => titleMap.get(id)?.title_name)
      .filter((n): n is string => !!n),
    company_sizes: config.company_sizes,
  };
}

export function resolveTargetAudienceStrings(
  config: IcpConfig,
  masterData: IcpMasterData
): {
  countries: string[];
  industries: string[];
  company_sizes: string[];
  decision_maker_titles: string[];
} {
  const resolved = resolveIcpNames(config, masterData);
  return {
    countries: resolved.countries,
    industries: resolved.industries,
    company_sizes: resolved.company_sizes,
    decision_maker_titles: resolved.titles,
  };
}

export function resolveApolloLocation(
  countrySelection: { country_id: number; region_ids: number[] },
  masterData: IcpMasterData
): string | null {
  const country = masterData.countries.find(c => c.id === countrySelection.country_id);
  if (!country) return null;

  if (countrySelection.region_ids.length > 0) {
    const region = country.regions.find(r => r.id === countrySelection.region_ids[0]);
    if (region) return region.apollo_location_name;
  }

  return country.apollo_location_name;
}

export function resolveApolloLocationsForCountry(
  countryName: string,
  icpConfig: IcpConfig,
  masterData: IcpMasterData
): { regions: string[]; regionNames: string[] } {
  const country = masterData.countries.find(c => c.country_name === countryName);
  if (!country) return { regions: [], regionNames: [] };

  const sel = icpConfig.countries.find(c => c.country_id === country.id);
  if (!sel || sel.region_ids.length === 0) return { regions: [], regionNames: [] };

  const regions: string[] = [];
  const regionNames: string[] = [];
  for (const rid of sel.region_ids) {
    const region = country.regions.find(r => r.id === rid);
    if (region) {
      regions.push(region.apollo_location_name);
      regionNames.push(region.region_name);
    }
  }
  return { regions, regionNames };
}
